/**
 * Acces d'urgence / heritage cote CLIENT (R32, conception
 * `docs/EMERGENCY-ACCESS-DESIGN.md`). Deux roles, deux moities de ce module :
 *
 * - le TITULAIRE genere un kit (256 bits CSPRNG), scelle sa `vaultKey` dessous,
 *   et n'envoie au serveur que le blob scelle + un verificateur. Le kit
 *   s'imprime et se remet HORS LIGNE au contact : il ne part jamais sur le
 *   reseau, et rien ici ne le persiste ;
 * - le CONTACT ne detient que ce kit. Il en derive sa preuve d'acces (domaine
 *   HKDF distinct de la cle de scellement : la preuve qu'il confie au serveur
 *   n'ouvre rien), declenche l'attente, puis retire le coffre chiffre et
 *   l'ouvre LOCALEMENT.
 *
 * Le serveur ne remet aucune session au contact : la lecture seule (arbitrage
 * §8.3) n'est pas une regle d'affichage, c'est l'absence de moyen d'ecrire.
 */
import {
  DecryptionFailedError,
  InvalidParameterError,
  deriveEmergencyAccessProof,
  emergencyAccessVerifier,
  formatEmergencyKit,
  generateEmergencyKit,
  parseEmergencyKit,
  openEmergencyAccess,
  type SecretKey,
} from '@klavox/crypto-core';
import {
  authenticateVaultRecords,
  decryptVaultFolder,
  decryptVaultItem,
  generateId,
  type VaultFolder,
  type VaultItem,
} from '@klavox/vault-model';
import {
  KlavoxApiError,
  createEmergencyGrant,
  listEmergencyGrants,
  refuseEmergencyGrant,
  releaseEmergencyAccess,
  requestEmergencyAccess,
  revokeEmergencyGrant,
  type EmergencyGrantSummary,
  type EmergencyRequestState,
} from './sync-client.js';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * Delais proposes a l'IHM. Bornes du serveur (24 h - 90 j) ; le defaut de
 * 7 jours est celui recommande par la conception (§8.2) : assez long pour
 * qu'un titulaire en vacances puisse refuser, assez court pour qu'une
 * succession ne s'enlise pas.
 */
export const EMERGENCY_WAIT_PRESETS: readonly { readonly label: string; readonly waitMs: number }[] = Object.freeze([
  { label: '24 heures', waitMs: DAY_MS },
  { label: '3 jours', waitMs: 3 * DAY_MS },
  { label: '7 jours (recommandé)', waitMs: 7 * DAY_MS },
  { label: '30 jours', waitMs: 30 * DAY_MS },
  { label: '90 jours', waitMs: 90 * DAY_MS },
]);

export const DEFAULT_EMERGENCY_WAIT_MS = 7 * DAY_MS;

/** Delegation telle que l'IHM du titulaire l'affiche : libelle du contact OUVERT localement. */
export interface EmergencyGrantView extends EmergencyGrantSummary {
  /** Nom du contact, dechiffre ici. `null` si le blob ne s'ouvre pas sous cette `vaultKey`. */
  readonly contactName: string | null;
}

interface ContactLabelSecrets {
  readonly name: string;
}

/**
 * Ce que ce module demande au coffre deverrouille, sans jamais voir la
 * `vaultKey` (meme frontiere que `send-client.ts` : `VaultStore` implemente ce
 * port, la cle ne sort pas de lui).
 */
export interface EmergencyVaultPort {
  sealForEmergencyKit(grantId: string, kit: SecretKey): string;
  sealEmergencyLabel(grantId: string, label: unknown): string;
  openEmergencyLabel<T>(grantId: string, blob: string): T;
}

export interface CreateEmergencyGrantInput {
  readonly baseUrl: string;
  readonly sessionToken: string;
  readonly vault: EmergencyVaultPort;
  readonly contactName: string;
  readonly waitMs?: number;
}

export interface CreatedEmergencyGrant {
  readonly grant: EmergencyGrantView;
  /**
   * Le kit, en base32 par groupes de 4 (comme la cle de recuperation). A
   * IMPRIMER et remettre en main propre : c'est le seul exemplaire, il n'est
   * ni stocke ici ni recuperable ensuite.
   */
  readonly kitDisplay: string;
}

/** Nom de contact borne : il finit dans un blob scelle, pas dans une base a mots de passe. */
export const MAX_CONTACT_NAME_LENGTH = 64;

export async function createEmergencyGrantWithKit(input: CreateEmergencyGrantInput): Promise<CreatedEmergencyGrant> {
  const contactName = input.contactName.trim();
  if (contactName.length === 0 || contactName.length > MAX_CONTACT_NAME_LENGTH) {
    throw new InvalidParameterError(`contactName: 1 a ${MAX_CONTACT_NAME_LENGTH} caracteres attendus`);
  }
  const waitMs = input.waitMs ?? DEFAULT_EMERGENCY_WAIT_MS;
  const id = generateId();
  const kit = generateEmergencyKit();
  try {
    const grant = await createEmergencyGrant(input.baseUrl, input.sessionToken, {
      id,
      // Le serveur ne recoit que le VERIFICATEUR : il ne verra la preuve qu'au
      // jour ou le contact s'en servira.
      verifier: emergencyVerifierFromKit(kit),
      protectedKey: input.vault.sealForEmergencyKit(id, kit),
      // Le nom du contact est un secret du titulaire : scelle sous SA vaultKey.
      contactLabel: input.vault.sealEmergencyLabel(id, { name: contactName }),
      waitMs,
    });
    return { grant: { ...grant, contactName }, kitDisplay: formatEmergencyKit(kit) };
  } finally {
    // L'exemplaire renvoye a l'appelant est une CHAINE ; la matiere secrete,
    // elle, ne survit pas a cet appel.
    kit.destroy();
  }
}

function emergencyVerifierFromKit(kit: SecretKey): string {
  // La preuve est derivee puis immediatement hachee : le titulaire ne la
  // conserve pas plus que le serveur.
  return emergencyAccessVerifier(deriveEmergencyAccessProof(kit));
}

/** Delegations du titulaire, libelles ouverts localement. Un libelle illisible n'efface pas la ligne. */
export async function readEmergencyGrants(input: {
  readonly baseUrl: string;
  readonly sessionToken: string;
  readonly vault: EmergencyVaultPort;
}): Promise<EmergencyGrantView[]> {
  const grants = await listEmergencyGrants(input.baseUrl, input.sessionToken);
  return grants.map((grant) => {
    try {
      const secrets = input.vault.openEmergencyLabel<ContactLabelSecrets>(grant.id, grant.contactLabel);
      return { ...grant, contactName: typeof secrets?.name === 'string' ? secrets.name : null };
    } catch {
      // Blob d'un autre coffre, ou altere par le serveur : la delegation reste
      // visible (et revocable) meme si son etiquette ne s'ouvre pas.
      return { ...grant, contactName: null };
    }
  });
}

export async function refuseEmergencyGrantById(
  baseUrl: string,
  sessionToken: string,
  id: string,
): Promise<EmergencyGrantSummary> {
  return refuseEmergencyGrant(baseUrl, sessionToken, id);
}

export async function revokeEmergencyGrantById(baseUrl: string, sessionToken: string, id: string): Promise<void> {
  await revokeEmergencyGrant(baseUrl, sessionToken, id);
}

/**
 * Voie du CONTACT -- declenche l'attente. Le kit saisi ne quitte pas cet
 * appareil : seule la preuve qui en derive part, et elle n'ouvre rien.
 */
export async function requestEmergencyAccessWithKit(input: {
  readonly baseUrl: string;
  readonly kitDisplay: string;
}): Promise<EmergencyRequestState> {
  const kit = parseEmergencyKit(input.kitDisplay);
  try {
    return await requestEmergencyAccess(input.baseUrl, deriveEmergencyAccessProof(kit));
  } finally {
    kit.destroy();
  }
}

/** Coffre du titulaire, ouvert par le contact. Lecture seule : aucune session n'existe pour le reecrire. */
export interface OpenedEmergencyVault {
  readonly grantId: string;
  readonly items: readonly VaultItem[];
  readonly folders: readonly VaultFolder[];
  /** Enregistrements rejetes a l'authentification (serveur non digne de confiance). */
  readonly rejected: number;
}

export async function openEmergencyVault(input: {
  readonly baseUrl: string;
  readonly kitDisplay: string;
}): Promise<OpenedEmergencyVault> {
  const kit = parseEmergencyKit(input.kitDisplay);
  let vaultKey: SecretKey | undefined;
  try {
    const released = await releaseEmergencyAccess(input.baseUrl, deriveEmergencyAccessProof(kit));
    vaultKey = openEmergencyAccess({ protectedKey: released.protectedKey, kit, grantId: released.grantId });
    // Meme discipline qu'a la recuperation de compte : ce serveur n'est pas
    // digne de confiance, seuls les enregistrements AUTHENTIQUES sont lus.
    const authentic = authenticateVaultRecords({ items: released.items, folders: released.folders, vaultKey });
    const items = authentic.items
      .filter((item) => item.deletedAt === null)
      .map((item) => decryptVaultItem({ item, vaultKey: vaultKey! }));
    const folders = authentic.folders
      .filter((folder) => folder.deletedAt === null)
      .map((folder) => decryptVaultFolder({ folder, vaultKey: vaultKey! }));
    return {
      grantId: released.grantId,
      items,
      folders,
      rejected:
        released.items.length - authentic.items.length + (released.folders.length - authentic.folders.length),
    };
  } finally {
    vaultKey?.destroy();
    kit.destroy();
  }
}

/** Message utilisateur sans oracle : « pas encore » et « inconnu » se disent pareil. */
export function describeEmergencyError(cause: unknown): string {
  if (cause instanceof InvalidParameterError) return 'kit d’accès d’urgence mal formé — vérifiez la saisie';
  if (cause instanceof DecryptionFailedError) return 'ce kit n’ouvre pas ce coffre';
  if (cause instanceof KlavoxApiError) {
    // Le serveur répond volontairement la MÊME chose pour « délai non écoulé »,
    // « refusé », « révoqué » et « inconnu » : ne pas inventer de distinction ici.
    if (cause.code === 'not_found') return 'aucun accès d’urgence disponible pour ce kit pour le moment';
    if (cause.code === 'quota_exceeded') return 'nombre maximal de contacts de confiance atteint';
    if (cause.code === 'session_expired') return 'session expirée — reconnectez-vous';
    if (cause.code === 'network_error') return 'serveur injoignable — vérifiez l’URL et votre connexion';
    if (cause.code === 'invalid_request') return 'requête refusée par le serveur';
  }
  return 'échec de l’accès d’urgence';
}
